<style scoped>
* {
  font-family: "poppins", sans-serif !important;
}

.login-page {
  min-height: 100vh;
  display: flex;
  background: #fff;
}

/* ─── Left branded panel ─── */
.login-left {
  flex: 1;
  background: linear-gradient(135deg, #0d1b5e 0%, #07124a 50%, #050c44 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 48px;
  position: relative;
  overflow: hidden;
}

.login-left::before {
  content: "";
  position: absolute;
  top: -120px;
  right: -120px;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: rgba(232, 114, 111, 0.08);
}

.login-left::after {
  content: "";
  position: absolute;
  bottom: -80px;
  left: -80px;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: rgba(232, 114, 111, 0.05);
}

.login-left-content {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 380px;
}

.login-logo-img {
  width: 80px;
  height: 80px;
  margin-bottom: 24px;
}

.login-left-title {
  font-size: 2rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 12px;
  letter-spacing: -0.5px;
}

.login-left-subtitle {
  font-size: 1rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin-bottom: 40px;
}

.login-feature-list {
  text-align: left;
  list-style: none;
  padding: 0;
  margin: 0;
}

.login-feature-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 16px;
}

.feature-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(232, 114, 111, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.feature-icon .v-icon {
  color: #E8726F !important;
  font-size: 18px !important;
}

/* ─── Right form panel ─── */
.login-right {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: #f8f9fc;
}

.login-form-wrap {
  width: 100%;
  max-width: 420px;
}

.login-form-card {
  background: #fff !important;
  border: none !important;
  border-radius: 16px !important;
  padding: 48px 40px !important;
}

.login-form-heading {
  font-size: 1.5rem;
  font-weight: 600;
  color: #050c44;
  margin-bottom: 4px;
}

.login-form-sub {
  color: #6b7280;
  font-size: 0.9rem;
  margin-bottom: 32px;
}

.field-label {
  font-weight: 500;
  font-size: 0.85rem;
  color: #3f4254;
  margin-bottom: 4px;
}

.login-sign-in-btn {
  text-transform: none !important;
  font-weight: 500 !important;
  font-size: 1rem !important;
  letter-spacing: 0 !important;
  height: 48px !important;
  border-radius: 8px !important;
}

.login-forgot-link {
  color: #050c44;
  font-size: 0.85rem;
  text-decoration: none;
  cursor: pointer;
  display: block;
  text-align: right;
}

.login-forgot-link:hover {
  text-decoration: underline;
}

/* ─── Responsive: stack on mobile ─── */
@media (max-width: 960px) {
  .login-page {
    flex-direction: column;
  }
  .login-left {
    padding: 40px 24px;
    min-height: 220px;
  }
  .login-left-title {
    font-size: 1.5rem;
  }
  .login-left-subtitle {
    margin-bottom: 0;
    font-size: 0.9rem;
  }
  .login-feature-list {
    display: none;
  }
  .login-right {
    padding: 24px;
  }
  .login-form-card {
    padding: 32px 24px !important;
  }
}
</style>

<style>
.login-page .v-text-field--outlined fieldset {
  border-color: #e4e6ef !important;
  border-radius: 8px !important;
}
.login-page .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #050c44 !important;
}
.login-page .v-input__slot {
  min-height: 48px !important;
}
.login-page .v-text-field input::placeholder {
  font-family: "poppins", sans-serif !important;
  font-size: 0.95rem;
  color: #9ca3af !important;
  font-weight: 400;
  opacity: 1 !important;
}
.login-page .v-input__prepend-inner .v-icon,
.login-page .v-input__append-inner .v-icon {
  color: #9ca3af !important;
  font-size: 20px !important;
}
.login-page .v-input__prepend-inner,
.login-page .v-input__append-inner {
  margin-top: 12px !important;
}
</style>

<template>
  <v-app>
    <v-overlay :value="overlay_load" z-index="1000">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <div class="login-page">
      <!-- ═══ Left branded panel ═══ -->
      <div class="login-left">
        <div class="login-left-content">
          <img
            src="@/assets/logo_without_background.png"
            alt="STARRboard"
            class="login-logo-img"
          />
          <h1 class="login-left-title">Welcome Back</h1>
          <p class="login-left-subtitle">
            Sign in to your STARRboard account and continue managing your business.
          </p>
          <ul class="login-feature-list">
            <li>
              <span class="feature-icon"><v-icon small>mdi-chart-line</v-icon></span>
              Performance dashboards
            </li>
            <li>
              <span class="feature-icon"><v-icon small>mdi-check-circle-outline</v-icon></span>
              Task &amp; checklist management
            </li>
            <li>
              <span class="feature-icon"><v-icon small>mdi-account-group-outline</v-icon></span>
              Team collaboration
            </li>
            <li>
              <span class="feature-icon"><v-icon small>mdi-shield-check-outline</v-icon></span>
              Secure, role-based access
            </li>
          </ul>
        </div>
      </div>

      <!-- ═══ Right form panel ═══ -->
      <div class="login-right">
        <div class="login-form-wrap">
          <v-card class="login-form-card" elevation="0">
            <h2 class="login-form-heading">Sign in to your account</h2>
            <p class="login-form-sub">Enter your credentials to continue</p>

            <div class="field-label">Email or Username <span style="color: #f1416c">*</span></div>
            <v-text-field
              v-model="email"
              placeholder="Enter your email or username"
              outlined
              prepend-inner-icon="mdi-email-outline"
              @keypress.enter="$refs.pwd_field.focus()"
              color="#050c44"
              hide-details
              style="margin-bottom: 20px"
              :disabled="overlay_load"
            />

            <div class="field-label">Password <span style="color: #f1416c">*</span></div>
            <v-text-field
              v-model="password"
              placeholder="Enter your password"
              :type="showPassword ? 'text' : 'password'"
              outlined
              ref="pwd_field"
              prepend-inner-icon="mdi-lock-outline"
              :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
              @click:append="showPassword = !showPassword"
              @keypress.enter="prefixflag ? $refs.prefix_field.focus() : validate()"
              color="#050c44"
              hide-details
              style="margin-bottom: 20px"
              :disabled="overlay_load"
            />

            <template v-if="prefixflag">
              <div class="field-label">Sub Domain <span style="color: #f1416c">*</span></div>
              <v-text-field
                v-model="prefix"
                placeholder="e.g. yourcompany"
                outlined
                ref="prefix_field"
                prepend-inner-icon="mdi-web"
                @keypress.enter="validate()"
                color="#050c44"
                hide-details
                style="margin-bottom: 20px"
                :disabled="overlay_load"
                @change="changetoLowercase(prefix)"
              />
            </template>

            <div class="d-flex justify-end" style="margin-bottom: 24px">
              <span class="login-forgot-link" @click="forgot">Forgot password?</span>
            </div>

            <v-btn
              block
              dark
              color="#E8726F"
              class="login-sign-in-btn"
              :loading="overlay_load"
              @click="validate()"
              elevation="0"
            >
              Sign In
            </v-btn>

            <p style="margin-top:14px;margin-bottom:0;font-size:11px;color:#9ca3af;text-align:center;line-height:1.5">
              By signing in, you agree to our
              <a href="/terms" target="_blank" style="color:#050c44;text-decoration:underline">Terms</a>
              &amp;
              <a href="/privacy" target="_blank" style="color:#050c44;text-decoration:underline">Privacy Policy</a>
            </p>
          </v-card>
        </div>
      </div>
    </div>
  </v-app>
</template>

<script>
import axios from "axios";
import Package from "/package.json";

export default {
  mounted() {
    let domain = window.location.host.split(".");
    if (
      domain.length === 0 ||
      domain == null ||
      domain == "" ||
      domain == undefined ||
      domain == "localhost"
    ) {
      this.prefixflag = true;
    }
  },
  data() {
    return {
      applicationVersion: Package.version,
      email: "",
      password: "",
      showPassword: false,
      overlay_load: false,
      prefix: null,
      prefixflag: false,
      logged_user_id: null,
    };
  },

  methods: {
    validate() {
      if (this.prefixflag) {
        if (!this.prefix) return;
      } else {
        let domain = window.location.host.split(".");
        this.prefix = domain[0];
      }

      this.overlay_load = true;
      window.axios = require("axios");

      let mail = this.email ? this.email.trim() : "";
      let pass_word = this.password ? this.password.trim() : "";

      const login_dataset = {
        email: mail,
        password: pass_word,
        prefix: "cmp_" + this.prefix,
      };

      axios
        .post("api/sessions/login", login_dataset)
        .then((response) => {
          this.Successdataset(response.data, this.prefix);
        })
        .catch((error) => {
          console.log(error);
          this.overlay_load = false;
          this.global_push_toast("error", "server error");
        });
    },

    Successdataset(data, domain) {
      localStorage.setItem("resultat", this.applicationVersion);
      if (data.result == "success") {
        localStorage.setItem("TOKEN", data.token);
        let features_list = [];
        let product_list = [];

        if (!["admin", "devadmin"].includes(domain)) {
          axios
            .get("tapi/get_features_by_role/" + data.user_id)
            .then((response) => {
              if (response.data.data[0].entity_list.length == 0) {
                this.overlay_load = false;
                alert("Client is Deactivated");
                localStorage.clear();
                return;
              }

              sessionStorage.removeItem("host_dialog_shown");
              this.getDomainLogo(domain);

              localStorage.setItem("entitydata", JSON.stringify(response.data.data[0].entity_list));
              localStorage.setItem("IS_ADMIN", response.data.is_admin.toString());

              let enty = response.data.data[0].entity_list;
              if (enty.length == 1) {
                localStorage.setItem("ENTITY_ID", enty[0].id);
                localStorage.setItem("entity_name", enty[0].name);
                localStorage.setItem("ENT_FINYEAR", enty[0].financial_year != null ? enty[0].financial_year : "__NA__");
              }

              let employee_dataset = response.data.emp_details;
              localStorage.setItem("Self_Rating_Setting", "yes");
              if (employee_dataset.length) {
                localStorage.setItem("LOGGED_EMP_ID", employee_dataset[0].id);
                localStorage.setItem("LOGGED_EMP_NO", employee_dataset[0].empno);
              } else {
                localStorage.setItem("LOGGED_EMP_ID", 0);
                localStorage.setItem("LOGGED_EMP_NO", 0);
              }

              let fl = response.data.data[0].roles_list;
              this.set_local_storage(response.data.modules_by_users, response.data.screen_by_users);

              let pl = [];
              let product_list_item = response.data.products_by_users;
              if (product_list_item.length != 0) {
                pl = product_list_item.map((a) => a.product_code);
              } else {
                pl = response.data.data[0].product_list;
              }

              features_list = (fl == "" || fl == null) ? [] : fl;
              product_list  = (pl == "" || pl == null) ? [] : pl;

              localStorage.setItem("tectalik_product_list", product_list);
              localStorage.setItem("features_list", features_list);
              localStorage.setItem("USER_ID", data.user_id);
              this.logged_user_id = data.user_id;
              localStorage.setItem("USER_EMAIL", data.user_email);
              localStorage.setItem("USER_NAME", data.user_name);
              localStorage.setItem("BRANCH_ID", response.data.data[0].branch_id);
              localStorage.setItem("domain", domain);
              localStorage.removeItem("is_family_book");

              let product_get = localStorage.getItem("tectalik_product_list");
              product_list = [];
              if (product_get != null && product_get != "" && product_get != undefined && product_get.length != 0) {
                product_list = product_get.split(",");
              }

              this.$router.push({ path: "/show_list" });
            })
            .catch((error) => console.log(error, "error"));
        } else {
          axios
            .get("api/get_screen_and_user_data_by_role/", { params: { user_id: data.user_id } })
            .then((response) => {
              localStorage.setItem("USER_TYPE", response.data.data.user_type_code);
              localStorage.setItem("USER_TYPES", response.data.data.user_type_code);
              localStorage.setItem("SCREEN_RIGHT_DATASET", JSON.stringify([]));
              localStorage.setItem("MODULE_RIGHTS_DATASET", JSON.stringify([]));
              localStorage.setItem("COMMON_SCREEN_RIGHT_DATASET", JSON.stringify(response.data.common_screen_dataset));
              localStorage.setItem("tectalik_product_list", product_list);
              localStorage.setItem("features_list", features_list);
              localStorage.setItem("TOKEN", data.token);
              localStorage.setItem("USER_ID", data.user_id);
              this.logged_user_id = data.user_id;
              localStorage.setItem("USER_EMAIL", data.user_email);
              localStorage.setItem("USER_NAME", data.user_name);
              localStorage.setItem("domain", domain);
              localStorage.removeItem("is_family_book");
              this.$router.push({ path: "/admin_landing" });
            })
            .catch(() => {
              this.overlay_load = false;
              alert("Invalid credentials");
            });
        }
      } else {
        this.overlay_load = false;
        alert("Invalid credentials");
      }
    },

    async getDomainLogo(domain) {
      try {
        const res = await this.$papi.get("get_domain_logo_by_domain", { params: { domain } });
        if (res.data.data && res.data.data.logo) {
          let logoUrl = res.data.data.logo.startsWith("https://")
            ? res.data.data.logo
            : `https://devcdn.tectalik.com/compliance/uploads/original_${res.data.data.logo}`;
          localStorage.setItem("logoUrl", logoUrl);
        }
      } catch (err) {
        console.error("Error fetching logo:", err);
      }
    },

    forgot() {
      let domain = window.location.host.split(".");
      if (domain[0] == "admin") {
        this.$router.push({ path: "/forgotpassword_admin?email=" + this.email });
      } else {
        this.$router.push({ path: "/forgotpassword?email=" + this.email });
      }
    },

    set_local_storage(module_dataset = [], screen_dataset = []) {
      localStorage.setItem("MODULE_RIGHTS_DATASET", JSON.stringify(module_dataset));
      for (let i = 0; i < screen_dataset.length; i++) {
        screen_dataset[i]["hide_all"] = false;
        screen_dataset[i]["module_name"] = false;
        for (let j = 0; j < module_dataset.length; j++) {
          if (screen_dataset[i].modules_rights_id == module_dataset[j].id) {
            screen_dataset[i]["hide_all"] = module_dataset[j].active;
            screen_dataset[i]["module_name"] = module_dataset[j].module_name;
          }
        }
      }
      localStorage.setItem("SCREEN_RIGHT_DATASET", JSON.stringify(screen_dataset));
    },

    changetoLowercase($subdomain) {
      this.prefix = $subdomain.toLowerCase().trim();
    },
  },
};
</script>
